import React, { useContext, useState, useEffect } from 'react';
import { Typography, Button } from '@one-thd/sui-atomic-components';
import {
  ExperienceContext,
  useConfigService,
} from '@thd-nucleus/experience-context';
import { useNavigate } from '@thd-olt-component-react/router';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  IB2BAddCheckingAccountRequest,
  TNewPayment,
} from '../../../models/b2b/payments';
import {
  getPaymentsErrorCode,
  getPaymentsErrorMessage,
} from '../../../utils/payments';
import { b2bPaymentsApi } from '../../../api/b2b/payments';
import { usePayments } from '../../../hooks/use-payments';
import { useModal } from '../../../hooks/use-modal';
import { useGetRecaptchaToken } from '../../../hooks/use-get-recaptcha-token';
import { InfoPopover } from '../../shared/popover/popover';
import { CardType } from '../../../constants/payments';
import {
  PROXTRA_DOLLARS_MESSAGE,
  GIFT_CARDS_MESSAGE,
} from '../../../constants/popover-content';
import { AddCardModal, IAddTenderProps } from '../add-tender/add-tender-modal';
import {
  ADD_GIFT_CARD,
  ADD_CHECKING_ACCOUNT,
  ADD_KEY_TAG,
} from '../../../constants/response-success-messages';
import {
  ERR_UNABLE_TO_SAVE,
  ERR_GENERIC,
} from '../../../constants/response-error-messages';
import { RecaptchaAction } from '../../../constants/recaptcha';
import { setOverlayEvent, setPageInfo } from '../../../utils/analytics';
import {
  setAddCardAnalytics,
  setOtherButtonSubmissionAnalytics,
} from '../../../utils/payments-analytics';
import {
  getSiteKey,
  getPaymentModalsVersion,
  getAddGiftCardSiteKey,
} from '../../../utils/general';
import {
  newrelic,
  NEW_RELIC_CHECKING_PREFIX,
  createNewRelicActions,
  NEW_RELIC_RSA_PREFIX,
} from '../../../utils/newrelic';
import { B2B_PURCHASE_GIFT_CARD_PATH } from '../../../constants/links';
import { useSearchParam } from '../../../hooks/use-search-param';
import { rsaEncryption } from '../../../utils/rsa-encryption';
import { paymentsApi } from '../../../api/common/payments';
import { PUBLIC_KEY_RETRIEVAL_FAILED } from '../../../constants/error-codes';

const cardTypesTitle: Record<CardType, string> = {
  [CardType.CREDIT_CARD]: 'Credit Cards',
  [CardType.PRO_XTRA_DOLLARS]: 'Pro Xtra Dollars',
  [CardType.GIFT_CARD]: 'Gift Cards',
  [CardType.PAYPAL_ACCOUNT]: 'PayPal',
  [CardType.CHECKING_ACCOUNT]: 'Checking Accounts',
  [CardType.KEY_TAG]: 'Keytags',
  [CardType.PRO_HOUSE_ACCOUNT]: 'Lines Of Credit',
} as const;

const cardTypesAddBtnLabel = {
  [CardType.CREDIT_CARD]: 'Credit Card',
  [CardType.GIFT_CARD]: 'Gift Card',
  [CardType.CHECKING_ACCOUNT]: 'Checking Account',
  [CardType.KEY_TAG]: 'Keytag',
} as const;

interface IPaymentHeaderProps {
  cardType: CardType;
  addBtn?: JSX.Element;
  isBulkAddAvailable?: boolean;
  onActionSuccess: (paymentId: string, message: string) => void;
  onActionFailure: (paymentId: string, message: string) => void;
}

const PaymentsHeaderContent = ({
  cardType,
  addBtn,
  isBulkAddAvailable = false,
  onActionFailure,
  onActionSuccess,
}: IPaymentHeaderProps) => {
  const { channel } = useContext(ExperienceContext);
  const { paginationInfo } = usePayments();
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
  const { openModal, setComponentProps, hideModal } = useModal();
  const navigate = useNavigate();
  const cardLength: Record<CardType, number> = {
    [CardType.CREDIT_CARD]: paginationInfo?.totalCreditCards || 0,
    [CardType.PRO_XTRA_DOLLARS]: paginationInfo?.totalProXtraDollars || 0,
    [CardType.GIFT_CARD]: paginationInfo?.totalGiftCards || 0,
    [CardType.PAYPAL_ACCOUNT]: paginationInfo?.totalPayPalAccounts || 0,
    [CardType.CHECKING_ACCOUNT]: paginationInfo?.totalCheckingAccounts || 0,
    [CardType.KEY_TAG]: paginationInfo?.totalKeyTags || 0,
    [CardType.PRO_HOUSE_ACCOUNT]: paginationInfo?.totalLinesOfCredit || 0,
  } as const;
  const checkingAction = createNewRelicActions(NEW_RELIC_CHECKING_PREFIX);
  const rsaAction = createNewRelicActions(NEW_RELIC_RSA_PREFIX);
  const isMobile = channel === 'mobile';
  const paymentModalsVersion = getPaymentModalsVersion();
  const fromProAllowance = useSearchParam('fromProAllowance') === 'true';
  const isBulkCardAddMicroUiEnabled = !!useConfigService(
    'BULK_CARD_ADD_MICRO_UI_ENABLED',
  );

  useEffect(() => {
    // bulk add operations only
    const iFrameListener = (event: MessageEvent) => {
      // iframe loaded
      if (event.data?.loaded) {
        window.postMessage(
          {
            event: 'experience data',
            siteKey: getSiteKey(true),
            experienceProps: window?.__EXPERIENCE_PROPS__,
            experienceContext: window?.__EXPERIENCE_CONTEXT__,
          },
          '*',
        );
      }

      if (event.data?.iframeClosed) {
        setIsBulkAddOpen(false);
      }

      if (event.data?.bulkResponse?.numberOfCardsSuccess > 0) {
        if (onActionSuccess) {
          onActionSuccess(
            '',
            `Success! All ${event.data?.bulkResponse.numberOfCardsSuccess} cards have been added to your account.`,
          );
        }

        setOtherButtonSubmissionAnalytics(
          `cards and accounts: bulk add success: ${event.data?.bulkResponse.numberOfCardsSuccess}}`,
        );
      }
    };

    // send info to iframe once it's loaded
    window.addEventListener('message', iFrameListener, false);
    return () => {
      window.removeEventListener('message', iFrameListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAddGiftCardRecaptchaEnabled = !!useConfigService(
    'IS_ADD_GIFTCARD_RECAPTCHA_ENABLED',
  );
  const isCheckingAuthRsaEncryptionEnabled: boolean = !!useConfigService(
    'CHECKING_AUTH_RSA_ENCRYPTION_ENABLED',
  );

  const recaptchaEnabled =
    cardType === CardType.GIFT_CARD && isAddGiftCardRecaptchaEnabled;
  const { getRecaptchaToken } = useGetRecaptchaToken(recaptchaEnabled);

  const getCheckingPublicKey = () => {
    const action = rsaAction.GET_RSA_PUBLIC_KEY;
    return paymentsApi
      .getRsaPublicKey()
      .then((PUBLIC_KEY) => {
        if (!PUBLIC_KEY) {
          newrelic.addPageAction(action, {
            result: 'error',
          });
          return null;
        }

        newrelic.addPageAction(action, {
          result: 'success',
        });

        return PUBLIC_KEY;
      })
      .catch(() => {
        newrelic.addPageAction(action, {
          result: 'error',
        });
        return null;
      });
  };

  const onAddPayment = async (
    cardNickName: string | undefined,
    cardNumber: string,
    routingNumber: string,
  ) => {
    let newPayment: TNewPayment;
    let successMessage: string;
    let analyticsCategory: 'gift card' | 'checking account' | 'keytag';
    let recaptchaToken: string;

    switch (cardType) {
      case CardType.GIFT_CARD:
        newPayment = {
          cardNickName: cardNickName || '',
          cardNumber,
          isActionSetAsDefault: false,
        };
        successMessage = ADD_GIFT_CARD.message;
        analyticsCategory = 'gift card';
        if (isAddGiftCardRecaptchaEnabled) {
          recaptchaToken = await getRecaptchaToken(
            RecaptchaAction.ADD_GIFT_CARD,
          );
        }
        break;

      case CardType.CHECKING_ACCOUNT:
        newPayment = {
          cardNickName: cardNickName || '',
          cardNumber,
          routingNumber,
          isActionSetAsDefault: false,
        };
        successMessage = ADD_CHECKING_ACCOUNT.message;
        analyticsCategory = 'checking account';
        break;

      case CardType.KEY_TAG:
        newPayment = {
          cardNickName,
          cardNumber,
          cardStatus: 'Active',
          isActionSetAsDefault: false,
        };
        if (cardNickName !== '' || cardNickName !== undefined)
          newPayment.cardNickName = cardNickName;
        successMessage = ADD_KEY_TAG.message;
        analyticsCategory = 'keytag';
        break;

      default:
        throw Error('Necessary payment info not provided');
    }

    const b2bPaymentsApiCall = () => {
      if (
        isCheckingAuthRsaEncryptionEnabled &&
        cardType === CardType.CHECKING_ACCOUNT
      ) {
        return getCheckingPublicKey()
          .then((PUBLIC_KEY) => {
            if (!PUBLIC_KEY) {
              throw new Error(PUBLIC_KEY_RETRIEVAL_FAILED);
            }
            return rsaEncryption(cardNumber, PUBLIC_KEY);
          })
          .then((encryptedAccountNumber) => {
            // New v2 addCheckingAccount endpoint payload
            const newPaymentEncrypted: IB2BAddCheckingAccountRequest['addCheckingAccountPaymentCardRequest'] =
              {
                encryptedAccountNumber,
                routingNumber,
                cardNickName: cardNickName || '',
              };
            return b2bPaymentsApi.addCheckingAccountWithEncryption(
              newPaymentEncrypted,
            );
          });
      }

      return b2bPaymentsApi.addPayment(newPayment, cardType, recaptchaToken);
    };

    const action = isCheckingAuthRsaEncryptionEnabled
      ? rsaAction.SUBMIT
      : checkingAction.SUBMIT;

    setComponentProps({ isLoading: true });
    b2bPaymentsApiCall()
      .then(() => {
        onActionSuccess('', successMessage);
        // cardType is CardType.GIFT_CARD | CardType.CHECKING_ACCOUNT | CardType.KEY_TAG so default and brand do no exist
        setAddCardAnalytics(analyticsCategory, false, '', '');
        if (cardType === CardType.CHECKING_ACCOUNT) {
          newrelic.addPageAction(action, {
            result: 'success',
          });
        }
      })
      .catch((error) => {
        const errCode = getPaymentsErrorCode(error);
        let errorMessage: string = ERR_UNABLE_TO_SAVE.message;

        // Handle RSA public key retrieval failure
        if (error?.message === PUBLIC_KEY_RETRIEVAL_FAILED) {
          errorMessage = ERR_GENERIC.message;
        }

        if (cardType === CardType.CHECKING_ACCOUNT) {
          newrelic.addPageAction(action, {
            errorCode: errCode,
            errMessage: getPaymentsErrorMessage(error),
            result: 'error',
          });
        }
        setPageInfo(
          'error',
          'inline',
          `add ${analyticsCategory}`,
          errorMessage,
          errCode || 'n/a',
        );
        onActionFailure('', errorMessage);
      })
      .finally(() => {
        setComponentProps({ isLoading: false });
        hideModal();
      });
  };

  const onAdd = () => {
    // handle add click for specific type
    if (cardType === CardType.GIFT_CARD) {
      const props: IAddTenderProps = {
        cardType: CardType.GIFT_CARD,
        isLoading: false,
        onAddCard: onAddPayment,
      };
      setOverlayEvent('add gift card');
      openModal(AddCardModal, props);
    }

    if (cardType === CardType.CHECKING_ACCOUNT) {
      const props: IAddTenderProps = {
        cardType: CardType.CHECKING_ACCOUNT,
        isLoading: false,
        onAddCard: onAddPayment,
      };
      setOverlayEvent('add checking account');
      openModal(AddCardModal, props);
    }

    if (cardType === CardType.KEY_TAG) {
      const props: IAddTenderProps = {
        cardType: CardType.KEY_TAG,
        isLoading: false,
        onAddCard: onAddPayment,
      };
      setOverlayEvent('add keytag');

      return openModal(AddCardModal, props);
    }

    if (cardType === CardType.CREDIT_CARD) {
      setOverlayEvent('add credit card');
      navigate(
        fromProAllowance
          ? '/myaccount/payments/addcreditcard?fromProAllowance=true'
          : '/myaccount/payments/addcreditcard',
      );
    }
    return '';
  };

  const getPopoverContent = (type: CardType) => {
    switch (type) {
      case CardType.PRO_XTRA_DOLLARS:
        return {
          title: PROXTRA_DOLLARS_MESSAGE.title,
          message: PROXTRA_DOLLARS_MESSAGE.message,
        };
      case CardType.GIFT_CARD:
        return {
          title: GIFT_CARDS_MESSAGE.title,
          message: GIFT_CARDS_MESSAGE.message,
        };
      default:
        return '';
    }
  };
  const popoverContent = getPopoverContent(cardType);
  const iconSize = isMobile ? 'small' : 'regular';
  const isGiftCard = cardType === CardType.GIFT_CARD;

  const renderAddButton = () => (
    <Button fullWidth onClick={onAdd} data-testid={`${cardType}-add-btn`}>
      Add {cardTypesAddBtnLabel[cardType as keyof typeof cardTypesAddBtnLabel]}
    </Button>
  );

  const iframeSrc = `/account/payment-ui/${paymentModalsVersion}/dist/client/bulk-add.html`;
  // local dev url
  // const iframeSrc = '/modals/bulk-add.html'

  return (
    <div className="sui-grid sui-grid-cols-1 md:sui-grid-cols-6 sui-gap-y-6 md:sui-gap-x-8 md:sui-gap-y-6 sui-mb-6 md:sui-mb-6 sui-items-start sui-items-top">
      <div className="sui-flex sui-flex-col sui-order-3 md:sui-order-1 sui-col-span-1 md:sui-col-span-3">
        {isGiftCard ? (
          <>
            <Typography weight="display" variant="h2" uppercase>
              {cardTypesTitle[cardType]} ({cardLength[cardType]})
            </Typography>

            <Typography variant="body-base">
              <div className="sui-pt-4">
                Saving a gift card allows you to track purchases made online and
                in-store with this card.
              </div>
            </Typography>
          </>
        ) : (
          <Typography weight="display" variant="h2" uppercase>
            {cardTypesTitle[cardType]} ({cardLength[cardType]})
            {popoverContent && (
              <InfoPopover iconSize={iconSize} title={popoverContent.title}>
                <Typography variant="body-base">
                  {popoverContent.message}
                </Typography>
              </InfoPopover>
            )}
          </Typography>
        )}
      </div>
      {isBulkAddAvailable && paymentModalsVersion && (
        <div className="sui-flex sui-order-2 md:sui-order-2 sui-col-span-1 sui-justify-end">
          <button
            type="button"
            onClick={() => {
              if (isBulkCardAddMicroUiEnabled) {
                navigate('/myaccount/payments/bulkaddcreditcards');
                return;
              }
              if (!isBulkAddOpen) {
                setIsBulkAddOpen(true);
                setOverlayEvent('bulk add credit cards');
              }
            }}
            data-testid="bulk-add-cards-btn"
          >
            <Typography decoration="underline">Bulk Add Cards</Typography>
          </button>
          {isBulkAddOpen && (
            <iframe
              title="Bulk Add Card Modal"
              src={iframeSrc}
              className="sui-fixed sui-z-[901] sui-top-0 sui-bottom-0 sui-left-0 sui-right-0 sui-w-screen sui-h-screen"
              data-testid="bulk-add-cards-modal"
            />
          )}
        </div>
      )}
      {addBtn ? (
        <div className="sui-flex sui-order-1 md:sui-order-3 sui-col-span-1 md:sui-col-span-2 md:sui-col-start-5">
          {addBtn}
        </div>
      ) : (
        cardType in cardTypesAddBtnLabel &&
        (isGiftCard ? (
          <div className="sui-col-span-3 md:sui-col-start-4 md:sui-order-3 lg:sui-col-start-5 lg:sui-col-span-2 2xl:sui-col-start-4 2xl:sui-col-span-3">
            <div className="sui-grid sui-grid-rows-2 sui-grid-flow-col sui-gap-y-4 2xl:sui-grid-rows-1 2xl:sui-grid-cols-3 2xl:sui-gap-x-8 2xl:sui-grid-flow-row">
              <div className="sui-order-2 2xl:sui-col-start-1 2xl:sui-col-span-1 2xl:-sui-ml-[32px]">
                <a href={B2B_PURCHASE_GIFT_CARD_PATH}>
                  <Button
                    fullWidth
                    style={{ minWidth: '177px' }}
                    variant="secondary"
                  >
                    Purchase Gift Card
                  </Button>
                </a>
              </div>
              <div className="sui-order-1 2xl:sui-order-3 2xl:sui-col-start-2 2xl:sui-col-span-2">
                {renderAddButton()}
              </div>
            </div>
          </div>
        ) : (
          <div className="sui-flex sui-order-1 md:sui-order-3 sui-col-span-1 md:sui-col-span-2 md:sui-col-start-5">
            {renderAddButton()}
          </div>
        ))
      )}
    </div>
  );
};

export const PaymentsHeader = ({
  cardType,
  addBtn,
  isBulkAddAvailable = false,
  onActionFailure,
  onActionSuccess,
}: IPaymentHeaderProps) => {
  const isAddGiftCardRecaptchaEnabled = !!useConfigService(
    'IS_ADD_GIFTCARD_RECAPTCHA_ENABLED',
  );
  if (cardType === CardType.GIFT_CARD && isAddGiftCardRecaptchaEnabled) {
    return (
      <GoogleReCaptchaProvider
        useEnterprise
        reCaptchaKey={getAddGiftCardSiteKey()}
      >
        <PaymentsHeaderContent
          cardType={cardType}
          addBtn={addBtn}
          isBulkAddAvailable={isBulkAddAvailable}
          onActionFailure={onActionFailure}
          onActionSuccess={onActionSuccess}
        />
      </GoogleReCaptchaProvider>
    );
  }
  return (
    <PaymentsHeaderContent
      cardType={cardType}
      addBtn={addBtn}
      isBulkAddAvailable={isBulkAddAvailable}
      onActionFailure={onActionFailure}
      onActionSuccess={onActionSuccess}
    />
  );
};
